/*
 * Checkout opens already matching the cake page: Pickup pre-selected, or the delivery address
 * filled in.
 *
 * WHY: checkout asked again. Customers who chose Delivery on the cake page switched to Pickup at
 * checkout by accident (the Telegram card's "⚠️ НЯМА адрес"), and everyone typed their address a
 * second time with no autocomplete (Shopify has none for Bulgaria). We cannot change checkout
 * without Plus, but the Storefront API can write preferences onto the theme's own cart, and
 * checkout reads them. Verified on the live checkout 2026-10-11 (docs/business-rules.md →
 * "Checkout address vs chosen neighbourhood").
 *
 * WHEN: on the cart page, which is the only way into checkout (the drawer has no checkout
 * button). Quantity changes and removals reload the page, so it re-syncs from the final cart.
 * A customer who taps checkout before the call lands simply gets today's empty checkout.
 *
 * WHICH CAKE: the first one in the cart. Two cakes with different handovers or addresses cannot
 * be expressed in one Shopify checkout anyway (Vlad, 2026-10-11).
 *
 * Fails silent: no token, network error, API change → checkout opens as it always has.
 */
const BH_STOREFRONT_TOKEN = 'a4bbec014892749de07902089f6d1b63'; // public by design, checkout scopes only

const BhCheckoutPrefill = {
  /**
   * Pickup has no shipping address for billing to copy, so Shopify shows an empty billing form
   * even for cash on the spot. Prefill it with the bakery (docs/business-rules.md → shop identity).
   * Card payers keep these fields editable; a фактура customer overwrites them.
   */
  PICKUP_BILLING: { address1: 'ул. Проф. Христо Вакарелски 11д', city: 'София', zip: '1172', country: 'BG' },

  /** "/cart?checkout[billing_address][…]=…" — the cart POST carries these into checkout (verified 2026-10-28). */
  pickupCheckoutAction() {
    const q = Object.entries(this.PICKUP_BILLING)
      .map(([k, v]) => `${encodeURIComponent(`checkout[billing_address][${k}]`)}=${encodeURIComponent(v)}`);
    return `/cart?${q.join('&')}`;
  },

  /** The first cake's handover + address, read off its line properties (BG or EN keys). */
  fromCart(cart) {
    const pick = (p, ...keys) => keys.map((k) => p[k]).find((v) => v && String(v).trim());
    const cake = (cart.items || []).find((i) => i.properties && pick(i.properties, 'Размер', 'Size'));
    if (!cake) return null;
    const p = cake.properties;
    const method = pick(p, 'Получаване', 'Handover Method');
    if (method === 'Вземане от място' || method === 'Pickup') return { pickup: true };
    if (method !== 'Доставка' && method !== 'Delivery') return null;
    const text = pick(p, 'Адрес за доставка', 'Delivery Address');
    if (!text) return null;
    const address = this.parseAddress(text);
    if (!address.zip && p._bh_zip) address.zip = p._bh_zip; // postcode of the Google pick
    return { pickup: false, address };
  },

  /**
   * "ж.к. Люлин 10, бул. „Европа“ 31, 1324 София, вх. Б, ап. 4" → checkout's fields. The part
   * before "<zip> <city>" is what Google suggested; anything after it is what the customer added.
   * Typed by hand with no postcode → the whole text is the street line, city София.
   */
  parseAddress(text) {
    const parts = String(text).split(',').map((s) => s.trim()).filter(Boolean);
    const i = parts.findIndex((s) => /^\d{4}\s+\S/.test(s));
    if (i < 1) return { address1: parts.join(', ').slice(0, 255), city: 'София', zip: '' };
    const [, zip, city] = parts[i].match(/^(\d{4})\s+(.+)$/);
    return {
      address1: parts.slice(0, i).join(', '),
      address2: parts.slice(i + 1).filter((s) => !/^(България|Bulgaria)$/i.test(s)).join(', '),
      city,
      zip,
    };
  },

  async sync() {
    try {
      const cart = await fetch('/cart.js').then((r) => r.json());
      const want = this.fromCart(cart);
      if (!want) return;
      // Only the checkout button: quantity changes submit the same form and must stay a plain /cart.
      document.querySelectorAll('button[name="checkout"]').forEach((b) => {
        if (want.pickup) b.setAttribute('formaction', this.pickupCheckoutAction());
        else b.removeAttribute('formaction');
      });
      const variables = { cartId: `gid://shopify/Cart/${cart.token}`, method: want.pickup ? 'PICK_UP' : 'SHIPPING' };
      let query = 'mutation($cartId: ID!, $method: PreferenceDeliveryMethodType!';
      if (want.pickup) {
        // Also drop an address an earlier Delivery sync left on the cart, so it cannot pull
        // checkout back to Доставка.
        query += `) {
          p: cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { countryCode: BG, preferences: { delivery: { deliveryMethod: [$method] } } }) { userErrors { message } }
          a: cartDeliveryAddressesReplace(cartId: $cartId, addresses: []) { userErrors { message } }
        }`;
      } else {
        query += `, $address: CartDeliveryAddressInput!) {
          p: cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: { countryCode: BG, preferences: { delivery: { deliveryMethod: [$method] } } }) { userErrors { message } }
          a: cartDeliveryAddressesReplace(cartId: $cartId, addresses: [{ selected: true, address: { deliveryAddress: $address } }]) { userErrors { message } }
        }`;
        variables.address = { ...want.address, countryCode: 'BG' };
      }
      const r = await fetch('/api/2025-07/graphql.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': BH_STOREFRONT_TOKEN },
        body: JSON.stringify({ query, variables }),
      }).then((res) => res.json());
      const errors = [r.errors, r.data?.p?.userErrors, r.data?.a?.userErrors].flat().filter(Boolean);
      if (errors.length) console.warn('bh-checkout-prefill', errors);
    } catch (e) {
      console.warn('bh-checkout-prefill', e);
    }
  },
};

// Loaded only by sections/main-cart.liquid. The guard lets scripts/test-checkout-prefill.mjs load it.
if (typeof document !== 'undefined') BhCheckoutPrefill.sync();
